import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';

import { SelectableHeaderReferenceDataComponent } from '@connect-services/client/components/modals/add-layer-modal/selectable-header-reference-data/selectable-header-reference-data.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { AddLayerService } from '@connect-services/client/services/add-layer.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { Deposit, DepositControllerGetRequestParams, DepositsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ExpansionPanelComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        SelectableHeaderReferenceDataComponent,
    ],
    selector: 'app-select-reference-deposit-step',
    templateUrl: 'select-reference-deposit-step.component.html',
})
export class SelectReferenceDepositStepComponent implements OnInit {
    protected readonly addLayerService = inject(AddLayerService);
    protected readonly deposits = signal<Deposit[]>([]);
    protected readonly isLoading = signal<boolean>(false);

    private readonly depositsService = inject(DepositsService);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly snackBarService = inject(SnackBarService);

    public async ngOnInit(): Promise<void> {
        const selectedFolder = this.addLayerService.selectedDepositFolder();
        if (!selectedFolder?.deposit) {
            this.deposits.set([]);

            return;
        }
        if (selectedFolder.deposit.version > 1) {
            await this.getDepositList(selectedFolder.id);

            return;
        }

        this.deposits.set([selectedFolder.deposit]);
    }

    protected toggleDepositSelected(deposit: Deposit): void {
        if (this.addLayerService.selectedDeposit() === deposit.id) {
            this.addLayerService.selectedDeposit.set(undefined);
            this.addLayerService.selectedDepositVersionAndName.set('');
            this.addLayerService.depositStandard.set(undefined);

            return;
        }

        this.addLayerService.selectedDepositVersionAndName.set(`${deposit.name} _ ${deposit.version}`);
        this.addLayerService.depositStandard.set(deposit.standard ?? undefined);
        this.addLayerService.selectedDeposit.set(deposit.id);
    }

    private async getDepositList(depositFolderId: string): Promise<void> {
        this.isLoading.set(true);
        try {
            const deposits: Deposit[] = await this.fetchAllService.getAll<DepositControllerGetRequestParams, Deposit>(
                this.depositsService.depositControllerGet.bind(this.depositsService),
                {
                    depositFolder: depositFolderId,
                    onlyReferenceDataDeposit: true,
                },
            );

            this.deposits.set([...deposits].sort(this.sortDepositsByVersion));
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }

    private sortDepositsByVersion(depositA: Deposit, depositB: Deposit): number {
        return depositA.version - depositB.version;
    }
}
